import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Drawer,
  Group,
  Loader,
  Skeleton,
  Stack,
  Stepper,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandWhatsapp,
  IconCheck,
  IconRosetteDiscountCheck,
  IconUsersPlus,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';
import { QueryError } from '../../components/common/QueryError';
import { useSetupState } from '../../components/onboarding/useSetupState';
import { BlockedByPlatform } from '../../components/onboarding/BlockedByPlatform';
import { SituationChoice } from '../../components/onboarding/SituationChoice';
import { CoexistenceTerms } from '../../components/onboarding/CoexistenceTerms';
import { VerifyLiveCard } from '../../components/onboarding/VerifyLiveCard';
import { ReceiveRepairCard } from '../../components/onboarding/ReceiveRepairCard';
import { ManualWhatsAppForm } from '../../components/onboarding/ManualWhatsAppForm';

/**
 * The guided setup corridor.
 *
 * One route rather than six, matching how `campaigns.new.tsx` and
 * `contacts.import.tsx` already do multi-step flows here: the step is derived
 * state, not a URL segment, so six route files would be boilerplate around a
 * value the state machine already owns. It also keeps the OAuth `?status=`
 * return handling in one place.
 *
 * It renders inside the vendor AppShell on purpose — the nav stays reachable,
 * so the corridor is a path and never a trap. Every step can be skipped.
 */

/** Ported from the existing Meta page so the redirect fallback still explains itself. */
function describeConnectStatus(status: string): string {
  switch (status) {
    case 'oauth-denied':
      return 'You cancelled the Meta connection.';
    case 'invalid-state':
    case 'state-expired':
      return 'That connection link expired. Please try again.';
    case 'vendor-mismatch':
      return 'That link was for a different account. Please try again from this workspace.';
    case 'missing-scopes':
      return 'Meta did not grant all the permissions we need. Please try again and accept them all.';
    default:
      return 'That connection did not complete. Please try again.';
  }
}

function SetupCorridor() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { status?: string };
  const utils = trpc.useUtils();

  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Which optional steps the vendor advanced past in this sitting. The derived
  // state machine owns steps 1-2; 3 and 4 are optional, so their progress is
  // local until it is persisted as a skip.
  const [extrasDone, setExtrasDone] = useState(false);

  // Poll only while the connection is unproven — the verify card has to flip by
  // itself when a reply lands, and nothing else here changes without a click.
  const setup = useSetupState({ poll: true });
  const methods = trpc.integrations.meta.onboardingMethods.useQuery();

  const saveState = trpc.integrations.meta.onboarding.saveState.useMutation({
    onSuccess: () => void utils.integrations.meta.onboarding.getState.invalidate(),
  });

  const embeddedSignupComplete = trpc.integrations.meta.embeddedSignupComplete.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'WhatsApp connected', color: 'green' });
      setup.refetch();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const channelSignupComplete = trpc.integrations.meta.channelSignupComplete.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Channel connected', color: 'green' });
      setup.refetch();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const startRedirect = trpc.integrations.meta.startEmbeddedSignup.useMutation();

  // The server-side redirect fallback returns here with ?status=...
  useEffect(() => {
    if (!search.status) return;
    setup.refetch();
    if (search.status.endsWith('-connected')) {
      notifications.show({ message: 'Connected', color: 'green' });
    } else {
      notifications.show({ message: describeConnectStatus(search.status), color: 'red' });
    }
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('status');
      window.history.replaceState({}, '', url.toString());
    }
    // Intentionally keyed on the status value alone: re-running on every
    // `setup` identity change would re-toast on each poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.status]);

  const o = methods.data;

  /** Both WhatsApp popup paths. `coexistence` only changes the extras we pass. */
  const launchWhatsApp = async (coexistence: boolean) => {
    if (!o) return;
    if (!o.appId || !o.configId) {
      // No JS-SDK config — fall back to the server-side redirect.
      const r = await startRedirect.mutateAsync({ channel: 'whatsapp' });
      window.location.href = r.url;
      return;
    }
    setBusy(true);
    try {
      const { launchEmbeddedSignup } = await import('../../lib/meta-embedded-signup');
      const result = await launchEmbeddedSignup({
        appId: o.appId,
        configId: o.configId,
        graphVersion: o.graphVersion,
        ...(coexistence ? { featureType: o.coexistenceFeatureType } : {}),
      });
      await embeddedSignupComplete.mutateAsync({
        code: result.code,
        wabaId: result.wabaId,
        phoneNumberId: result.phoneNumberId,
        businessId: result.businessId,
        // Meta may silently grant a standard connection when a number turns out
        // to be ineligible, so only the app-onboarding event confirms
        // coexistence. Telling the backend otherwise would mislabel the source.
        mode: coexistence && result.isAppOnboarding ? 'coexistence' : 'new',
      });
    } catch (err) {
      notifications.show({ message: (err as Error).message, color: 'red' });
    } finally {
      setBusy(false);
    }
  };

  const launchChannel = async (channel: 'facebook' | 'instagram') => {
    if (!o) return;
    const configId = channel === 'facebook' ? o.facebookConfigId : o.instagramConfigId;
    if (!o.appId || !configId) {
      const r = await startRedirect.mutateAsync({ channel });
      window.location.href = r.url;
      return;
    }
    setBusy(true);
    try {
      const { launchBusinessLogin } = await import('../../lib/meta-embedded-signup');
      const { code } = await launchBusinessLogin({
        appId: o.appId,
        configId,
        graphVersion: o.graphVersion,
      });
      await channelSignupComplete.mutateAsync({ channel, code });
    } catch (err) {
      notifications.show({ message: (err as Error).message, color: 'red' });
    } finally {
      setBusy(false);
    }
  };

  const skip = (step: number) => saveState.mutate({ skippedSteps: [step] });

  const finish = () => {
    saveState.mutate({ completedAt: new Date().toISOString() });
    void navigate({ to: '/vendor' });
  };

  if (setup.isPending) {
    return (
      <Stack gap="md" maw={880} mx="auto">
        <Skeleton height={40} radius="md" />
        <Skeleton height={220} radius="md" />
      </Stack>
    );
  }

  if (setup.isError || !setup.snapshot) {
    return (
      <Stack maw={880} mx="auto">
        <QueryError
          message="Couldn't load your setup status."
          detail={setup.error?.message}
          onRetry={setup.refetch}
        />
      </Stack>
    );
  }

  const s = setup.snapshot;
  // Steps 3 and 4 are optional, so once WhatsApp is proven we move the stepper
  // forward only as far as the vendor has actually gone.
  const activeStep = s.state === 'LIVE' ? (extrasDone ? 2 : 1) : s.step === 2 ? 1 : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg" maw={880} mx="auto" pb="xl">
        <Stack gap={4}>
          <Title order={2}>Let&apos;s get you set up</Title>
          <Text c="dimmed" size="sm">
            About three minutes. You can skip anything and come back later.
          </Text>
        </Stack>

        <Stepper
          active={activeStep}
          size="sm"
          styles={{
            stepBody: { marginLeft: 8 },
            stepLabel: { fontWeight: 600, fontSize: 14 },
            stepDescription: { fontSize: 12 },
          }}
        >
          <Stepper.Step
            label="Connect WhatsApp"
            description="The main event"
            icon={<IconBrandWhatsapp size={16} />}
          />
          <Stepper.Step
            label="Prove it works"
            description="Send yourself a test"
            icon={<IconRosetteDiscountCheck size={16} />}
          />
          <Stepper.Step
            label="You're live"
            description="What to do next"
            icon={<IconCheck size={16} />}
          />
        </Stepper>

        {/* ── Step 1 ─────────────────────────────────────────────── */}
        {s.state === 'BLOCKED_BY_PLATFORM' && s.blockedReason && (
          <BlockedByPlatform reason={s.blockedReason} />
        )}

        {s.state === 'NOT_CONNECTED' && o && (
          <Stack gap="md">
            <Card withBorder radius="md" padding="lg">
              <Stack gap="md">
                <Title order={4}>Do you already use WhatsApp Business on your phone?</Title>
                <SituationChoice
                  coexistenceAvailable={o.coexistenceEnabled}
                  embeddedSignupAvailable={o.embeddedSignupEnabled}
                  manualAvailable={o.manualEnabled}
                  busy={busy || embeddedSignupComplete.isPending}
                  onKeepMyNumber={() => void launchWhatsApp(true)}
                  onNewNumber={() => void launchWhatsApp(false)}
                  onManual={() => setManualOpen(true)}
                />
              </Stack>
            </Card>

            {o.coexistenceEnabled && (
              <Card withBorder radius="md" padding="lg">
                <Stack gap="sm">
                  <Text fw={600} size="sm">
                    If you keep your existing number
                  </Text>
                  <CoexistenceTerms />
                </Stack>
              </Card>
            )}

            <Group justify="flex-end">
              <Anchor component={Link} to="/vendor" size="sm" c="dimmed" onClick={() => skip(1)}>
                I&apos;ll do this later
              </Anchor>
            </Group>
          </Stack>
        )}

        {s.state === 'CONNECTING' && (
          <Card withBorder radius="md" padding="lg">
            <Group gap="sm" wrap="nowrap">
              <Loader size="sm" />
              <Stack gap={2}>
                <Text fw={600}>Meta is verifying your number</Text>
                <Text size="sm" c="dimmed">
                  {s.primaryNumber?.displayPhoneNumber ?? 'Your number'} is connected. This usually
                  takes under a minute — the page will move on by itself.
                </Text>
              </Stack>
            </Group>
          </Card>
        )}

        {/* ── Step 2 ─────────────────────────────────────────────── */}
        {s.state === 'CANNOT_RECEIVE' && (
          <ReceiveRepairCard needsAppSecret={s.needsAppSecret} onRepaired={setup.refetch} />
        )}

        {s.state === 'UNPROVEN' && (
          <Stack gap="md">
            <VerifyLiveCard />
            <Group justify="flex-end">
              <Anchor
                component="button"
                type="button"
                size="sm"
                c="dimmed"
                onClick={() => {
                  skip(2);
                  void navigate({ to: '/vendor' });
                }}
              >
                Skip this step
              </Anchor>
            </Group>
          </Stack>
        )}

        {/* ── Steps 3-5 ──────────────────────────────────────────── */}
        {s.state === 'LIVE' && !extrasDone && (
          <Stack gap="md">
            <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
              <Text size="sm" fw={500}>
                WhatsApp is working
                {s.primaryNumber?.displayPhoneNumber
                  ? ` on ${s.primaryNumber.displayPhoneNumber}`
                  : ''}
                .
              </Text>
            </Alert>

            <Card withBorder radius="md" padding="lg">
              <Stack gap="md">
                <Stack gap={2}>
                  <Text fw={600}>Add your other channels</Text>
                  <Text size="sm" c="dimmed">
                    Optional. You can do this any time from Connections.
                  </Text>
                </Stack>
                <Group>
                  <Button
                    variant="light"
                    leftSection={<IconBrandFacebook size={16} />}
                    loading={busy}
                    onClick={() => void launchChannel('facebook')}
                  >
                    Connect Messenger
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconBrandInstagram size={16} />}
                    loading={busy}
                    onClick={() => void launchChannel('instagram')}
                  >
                    Connect Instagram
                  </Button>
                </Group>
              </Stack>
            </Card>

            <Card withBorder radius="md" padding="lg">
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Text fw={600}>Bring in your customers</Text>
                  <Text size="sm" c="dimmed">
                    Import a spreadsheet of contacts to message.
                  </Text>
                </Stack>
                <Button
                  variant="light"
                  leftSection={<IconUsersPlus size={16} />}
                  component={Link}
                  to="/vendor/contacts/import"
                >
                  Import contacts
                </Button>
              </Group>
            </Card>

            <Group justify="flex-end">
              <Anchor
                component="button"
                type="button"
                size="sm"
                c="dimmed"
                onClick={() => {
                  skip(3);
                  skip(4);
                  setExtrasDone(true);
                }}
              >
                Skip for now
              </Anchor>
              <Button onClick={() => setExtrasDone(true)}>Continue</Button>
            </Group>
          </Stack>
        )}

        {s.state === 'LIVE' && extrasDone && (
          <Card withBorder radius="md" padding="lg">
            <Stack gap="md">
              <Stack gap={2}>
                <Title order={3}>You&apos;re live</Title>
                <Text size="sm" c="dimmed">
                  WhatsApp
                  {s.primaryNumber?.displayPhoneNumber
                    ? ` · ${s.primaryNumber.displayPhoneNumber}`
                    : ''}
                  {s.primaryNumber?.verifiedName ? ` · ${s.primaryNumber.verifiedName}` : ''}
                </Text>
              </Stack>

              <Text size="sm" fw={500}>
                What next?
              </Text>
              <Group>
                <Button component={Link} to="/vendor/chat" variant="light">
                  Open your inbox
                </Button>
                <Button component={Link} to="/vendor/templates" variant="light">
                  Create a template
                </Button>
                <Button component={Link} to="/vendor/campaigns" variant="light">
                  Plan a campaign
                </Button>
              </Group>

              <Group justify="flex-end">
                <Button onClick={finish}>Go to dashboard</Button>
              </Group>
            </Stack>
          </Card>
        )}

        <Drawer
          opened={manualOpen}
          onClose={() => setManualOpen(false)}
          position="right"
          size="md"
          title="Connect with your own Cloud API access"
        >
          <ManualWhatsAppForm
            onConnected={() => {
              setManualOpen(false);
              setup.refetch();
            }}
          />
        </Drawer>
      </Stack>
    </motion.div>
  );
}

function SetupGated() {
  return (
    <PermissionGate permission="manage_integrations">
      <SetupCorridor />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/setup')({ component: SetupGated });
